import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Button } from "../../ui/button";
import { Plus, Pencil, Trash2, FileText } from "lucide-react";
import { Methodology } from "../types";
import { AssetType, InputType } from "../../../types";
import { MethodologySidePanel } from "./MethodologySidePanel";
import { useToast } from "../../../hooks/use-toast";
import { getAssetTypeLabel, getInputTypeLabel } from "../utils/methodologyUtils";

interface MethodologyListProps {
  methodologies: Methodology[];
  onEdit: (methodology: Methodology) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onAdd: (methodology: Methodology) => Promise<void>;
}

export function MethodologyList({
  methodologies,
  onEdit,
  onDelete,
  onAdd,
}: MethodologyListProps) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedMethodology, setSelectedMethodology] = useState<Methodology | null>(null);
  const { toast } = useToast();

  const handleEdit = (methodology: Methodology) => {
    setSelectedMethodology(methodology);
    setIsPanelOpen(true);
  };

  const handleAdd = () => {
    setSelectedMethodology(null);
    setIsPanelOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await onDelete(id);
    } catch (error) {
      console.error('Error in handleDelete:', error);
      toast({
        title: "Error",
        description: "Failed to delete methodology",
        variant: "destructive",
      });
    }
  };

  const handlePanelSubmit = async (data: Partial<Methodology>) => {
    try {
      console.log('Panel submit handler called with data:', data);
      if (selectedMethodology) {
        console.log('Updating methodology:', { ...data, id: selectedMethodology.id });
        await onEdit({ ...data, id: selectedMethodology.id } as Methodology);
      } else {
        console.log('Adding new methodology:', data);
        await onAdd(data as Methodology);
      }
      setIsPanelOpen(false);
    } catch (error) {
      console.error('Error in handlePanelSubmit:', error);
      toast({
        title: "Error",
        description: selectedMethodology ? "Failed to update methodology" : "Failed to add methodology",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center">
        <Button onClick={handleAdd}>
          <Plus className="mr-2 h-4 w-4" />
          Add Methodology
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Domain</TableHead>
              <TableHead>Asset Type</TableHead>
              <TableHead>Project Input Type</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {methodologies.map((methodology) => (
              <TableRow key={methodology.id}>
                <TableCell>{methodology.name}</TableCell>
                <TableCell>{methodology.domain}</TableCell>
                <TableCell>{getAssetTypeLabel(methodology.asset_type as AssetType)}</TableCell>
                <TableCell>{getInputTypeLabel(methodology.project_input_type as InputType)}</TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(methodology)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(methodology.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                    {methodology.url && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(methodology.url, '_blank')}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <MethodologySidePanel
        open={isPanelOpen}
        onOpenChange={setIsPanelOpen}
        methodology={selectedMethodology}
        onSave={handlePanelSubmit}
      />
    </div>
  );
} 